// Syft ACP - Core <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import { datadogRum } from '@datadog/browser-rum-slim'
import type { AsyncStorage } from 'redux-persist'

import { purgeUnpersistedKeys } from 'syft-acp-core/reducers/persistence'

type OnComplete<T> = (err?: unknown, result?: T) => void

type Outcome = 'failed' | 'recovered' | 'recovered-without-purge' | 'unrecoverable'

/**
 * Run a callback on a macrotask, so a successful read or write calls back
 * asynchronously.
 *
 * It has to be a macrotask, not a microtask. redux-persist's own engine defers
 * with `setImmediate`, so rehydration finishes a full task later than a
 * `Promise.resolve().then` would. Shortening that moves the whole boot forward:
 * `initStore` resolves sooner, the app renders sooner, and a route that
 * redirects to a default child tab lands on the child before anything watching
 * the URL sees the parent. The Cypress smoke specs assert exactly that
 * intermediate URL, and a microtask here broke `/shifts` and `/finance`.
 *
 * The error paths below call back synchronously instead, which also matches
 * that engine: `setImmediate` on success, a bare `cb(e)` inside the catch.
 */
const defer = (fn: () => void): void => {
  setTimeout(fn, 0)
}

/**
 * A minimal async localStorage engine for redux-persist that reads
 * `window.localStorage` live on every call.
 *
 * redux-persist ships its own (`redux-persist/storages`), but that one probes
 * storage with a test write when its module is first imported, and on a full
 * origin the probe throws and the engine is silently swapped for a stub that
 * no-ops every write and reports no keys. Because the swap happens at import,
 * before `initStore` runs the boot sweep, the exact origins this guard exists
 * for would get the stub: rehydration would restore nothing and the new token
 * would be dropped, with no rejection for the guard below to act on.
 *
 * Reading `window.localStorage` directly has no probe and no stub. A full
 * origin throws `QuotaExceededError` from `setItem`, which the guard turns into
 * a sweep-and-retry, and reads see whatever is actually there.
 */
const webLocalStorage: AsyncStorage = {
  getAllKeys: cb =>
    new Promise<string[]>((resolve, reject) => {
      try {
        const keys: string[] = []
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i)
          if (key !== null) keys.push(key)
        }
        defer(() => {
          cb?.(null, keys)
          resolve(keys)
        })
      } catch (e) {
        cb?.(e)
        reject(e)
      }
    }),

  getItem: (key, cb) =>
    new Promise<string>((resolve, reject) => {
      try {
        const value = window.localStorage.getItem(key)
        defer(() => {
          cb?.(null, value ?? undefined)
          resolve(value as string)
        })
      } catch (e) {
        cb?.(e)
        reject(e)
      }
    }),

  removeItem: (key, cb) =>
    new Promise<void>((resolve, reject) => {
      try {
        window.localStorage.removeItem(key)
        defer(() => {
          cb?.(null)
          resolve()
        })
      } catch (e) {
        cb?.(e)
        reject(e)
      }
    }),

  setItem: (key, value, cb) =>
    new Promise<void>((resolve, reject) => {
      try {
        window.localStorage.setItem(key, value)
        defer(() => {
          cb?.(null)
          resolve()
        })
      } catch (e) {
        // A full origin lands here with a QuotaExceededError.
        cb?.(e)
        reject(e)
      }
    }),
}

/**
 * A DOMException from localStorage when the origin is out of space. Safari used
 * a different name and legacy code 22, so both are checked.
 */
const isQuotaError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false
  const { name, code } = err as { name?: string; code?: number }

  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || code === 22
}

const report = (key: string, outcome: Outcome, err: unknown, purgedKeys?: number) => {
  datadogRum.addError(err instanceof Error ? err : new Error(`localStorage write failed for ${key}`), {
    storageKey: key,
    outcome,
    purgedKeys,
  })
}

/**
 * Wraps the localStorage engine so a full origin cannot silently cost the user
 * their session.
 *
 * The engine rejects on a failed write and redux-persist attaches no handler to
 * the promise it returns, so the browser reports an unhandled rejection and the
 * app carries on as though the write succeeded. When the failing key was
 * `flex.auth` that meant a freshly issued SSO token was quietly dropped, and the
 * next page load rehydrated the previous expired one and bounced the user back
 * through Okta, with nothing in the UI to explain it.
 *
 * On a quota error this sweeps the keys ACP wrote for slices it no longer
 * persists, then retries once. That recovers the common case, where the space is
 * held by caches written before this app version. The outcome is always
 * reported, because a session write that needed a sweep to land is worth knowing
 * about even when the retry works.
 *
 * @param sliceNames the slices the root reducer produces, which is what the
 *   sweep uses to tell ACP's own keys apart from those of other apps sharing
 *   the origin
 */
export const createQuotaGuardedStorage = (
  sliceNames: readonly string[],
  base: AsyncStorage = webLocalStorage,
): AsyncStorage => ({
  getItem: (key, onComplete) => base.getItem(key, onComplete),
  removeItem: (key, onComplete) => base.removeItem(key, onComplete),
  getAllKeys: onComplete => base.getAllKeys(onComplete),

  setItem: (key: string, value: string, onComplete?: OnComplete<string>): Promise<void> =>
    // Two-argument `then` rather than `.catch`, so a throw from the success
    // handler is not mistaken for a failed write.
    base.setItem(key, value).then(
      () => {
        // The engine this wraps calls back on success too; keep that contract.
        onComplete?.()
      },
      (err: unknown) => {
        if (!isQuotaError(err)) {
          report(key, 'failed', err)
          onComplete?.(err)

          return
        }

        // Guarded: if enumerating storage itself throws we must still reach the
        // retry, so the real error surfaces via onComplete rather than as an
        // unhandled rejection, which is the failure mode this wrapper removes.
        let purged: string[] = []
        try {
          purged = purgeUnpersistedKeys(window.localStorage, sliceNames)
        } catch {
          purged = []
        }

        return base.setItem(key, value).then(
          () => {
            report(key, purged.length > 0 ? 'recovered' : 'recovered-without-purge', err, purged.length)
            onComplete?.()
          },
          (retryErr: unknown) => {
            report(key, 'unrecoverable', retryErr, purged.length)
            onComplete?.(retryErr)
          },
        )
      },
    ),
})
